/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FlexColumn, FlexRow, Typography } from 'design-system';

/**
 * Tablet / mobile counterpart to {@link ProgressSidebar} — the same steps as a
 * segmented bar above the content. Both are rendered; `learning.css` shows one
 * per breakpoint.
 */
export default function CompactProgressStepper({ steps }) {
  const intl = useIntl();

  if (!steps?.length) {
    return null;
  }

  const foundIndex = steps.findIndex((step) => step.status === 'active');
  const activeIndex = foundIndex === -1 ? 0 : foundIndex;
  const stepLabel = intl.formatMessage(
    { id: 'ld-step-of' },
    { current: activeIndex + 1, total: steps.length },
  );
  const currentTitle = steps[activeIndex]?.title;

  return (
    <FlexColumn
      align="stretch"
      fullWidth
      gap="var(--ds-space-2)"
      className="ld-step-progress"
    >
      <FlexRow
        align="center"
        justify="between"
        fullWidth
        wrap
        gap="var(--ds-space-2)"
      >
        <Typography
          component="p"
          variant="bodyExtraSmall"
          fontWeight="semibold"
          className="ld-stepper__heading"
        >
          {intl.formatMessage({ id: 'ld-your-progress' })}
        </Typography>
        <Typography
          component="p"
          variant="bodySmall"
          color="ui-subtle"
          className="ld-step-progress__label"
        >
          {currentTitle ? `${stepLabel} · ${currentTitle}` : stepLabel}
        </Typography>
      </FlexRow>

      <FlexRow
        align="stretch"
        fullWidth
        gap="var(--ds-space-1)"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={steps.length}
        aria-valuenow={activeIndex + 1}
        aria-valuetext={currentTitle ? `${stepLabel} · ${currentTitle}` : stepLabel}
      >
        {steps.map((step, index) => (
          <span
            key={step.id}
            className={[
              'ld-step-progress__segment',
              index <= activeIndex ? 'ld-step-progress__segment--filled' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-hidden
          />
        ))}
      </FlexRow>
    </FlexColumn>
  );
}

CompactProgressStepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      status: PropTypes.oneOf(['completed', 'active', 'pending', 'locked']).isRequired,
    }),
  ),
};
